import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { type User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import {
  registerWithEmailAndPassword,
  loginWithEmailAndPassword,
  loginWithGoogle,
  loginWithApple,
  resetPassword,
  logout,
  getCurrentUserProfile,
  type UserProfile,
  type AuthErrorType,
} from '../firebase/auth';
import { UserDataService } from '../firebase/userProfile';
import { type ServiceTierLevel } from '../../shared/types/base';

// Auth Context Interface
interface AuthContextType {
  currentUser: User | null;
  user: User | null; // Alias for currentUser for compatibility
  userProfile: UserProfile | null;
  loading: boolean;
  signup: (
    email: string,
    password: string,
    displayName?: string
  ) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resetUserPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider = React.memo<AuthProviderProps>(({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<void> => {
    try {
      await registerWithEmailAndPassword(email, password, displayName);
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    try {
      await loginWithEmailAndPassword(email, password);
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Google sign in function
  const signInWithGoogle = async (): Promise<void> => {
    try {
      await loginWithGoogle();
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Apple sign in function
  const signInWithApple = async (): Promise<void> => {
    try {
      await loginWithApple();
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Reset password function
  const resetUserPassword = async (email: string): Promise<void> => {
    try {
      await resetPassword(email);
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Sign out function
  const signOut = async (): Promise<void> => {
    try {
      await logout();
    } catch (error) {
      throw error as AuthErrorType;
    }
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      setUserProfile(getCurrentUserProfile(user));

      // Initialize user data in Firestore if user exists and it's their first time
      if (user) {
        try {
          // Check if user profile exists, if not, initialize it
          const userData = await UserDataService.getCompleteUserData(user.uid);
          if (!userData.profile) {
            // Extract social profile data if available
            const socialData = (
              user as unknown as {
                _socialProfileData?: { provider: string; providerId: string };
              }
            )._socialProfileData;

            // Prepare enhanced user data from social login
            const enhancedUserData = {
              displayName: user.displayName || user.email!.split('@')[0],
              photoURL: user.photoURL,
              phoneNumber: user.phoneNumber,
              // Add social provider info
              socialProvider: socialData?.provider,
              providerId: socialData?.providerId,
              // Try to infer some travel preferences from social data
              travelPreferences: {
                homeZipCode: '', // Will need to be filled in by user
                preferredAirports: [], // Could potentially infer from location data
              },
            };

            await UserDataService.initializeUserData(
              user.uid,
              user.email!,
              enhancedUserData.displayName,
              enhancedUserData.travelPreferences,
              {
                photoURL: enhancedUserData.photoURL || undefined,
                phoneNumber: enhancedUserData.phoneNumber || undefined,
                socialProvider: enhancedUserData.socialProvider,
                providerId: enhancedUserData.providerId,
              }
            );
          }
        } catch (error) {
          console.warn('Failed to initialize user data:', error);
          // Don't block authentication if user data initialization fails
        }
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Context value
  const value: AuthContextType = {
    currentUser,
    user: currentUser, // Alias for compatibility
    userProfile,
    loading,
    signup,
    login,
    signInWithGoogle,
    signInWithApple,
    resetUserPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});

AuthProvider.displayName = 'AuthProvider';
